import { useEffect, useRef, useState } from 'react'
import Hls, { HlsConfig } from 'hls.js'
import { logger } from '@/utils/logging'

interface UseHlsInstanceOptions {
  /** The video element ref to attach HLS.js to. */
  videoRef: React.RefObject<HTMLVideoElement | null>
  /** The HLS manifest URL to load. */
  url: string
  /** Whether the video should start muted. */
  muted?: boolean
  /** Optional HLS.js configuration (e.g., DRM settings in Phase 6). */
  hlsConfig?: Partial<HlsConfig>
}

interface UseHlsInstanceResult {
  /** Ref to the HLS.js instance. Read `.current` inside effects gated by `isReady`. */
  hlsRef: React.RefObject<Hls | null>
  /** Whether the source has been parsed and the player is ready for playback. */
  isReady: boolean
}

/**
 * Shared hook for initializing and managing an HLS.js instance.
 *
 * Handles:
 * - HLS.js support detection with Safari native HLS fallback
 * - Source loading and media attachment
 * - Fatal error recovery (network retry, media error recovery)
 * - Muted state application after manifest parse
 * - Cleanup on unmount or URL change
 *
 * Returns `hlsRef` (a stable ref) instead of the instance directly, because
 * the HLS instance is created inside a useEffect and must not trigger re-renders.
 * Use the `isReady` boolean to know when `hlsRef.current` is available.
 */
export function useHlsInstance({ videoRef, url, muted, hlsConfig }: UseHlsInstanceOptions): UseHlsInstanceResult {
  const hlsRef = useRef<Hls | null>(null)
  const [isReady, setIsReady] = useState(false)
  // Stable refs so prop changes don't destroy and recreate the HLS instance.
  // The hook creates HLS once on mount. URL changes (e.g., episode switching)
  // are handled by controller.load() in the consuming component.
  const urlRef = useRef(url)
  urlRef.current = url
  const mutedRef = useRef(muted)
  mutedRef.current = muted
  const hlsConfigRef = useRef(hlsConfig)
  hlsConfigRef.current = hlsConfig

  useEffect(() => {
    const video = videoRef.current
    const initialUrl = urlRef.current
    if (!video || !initialUrl) return

    setIsReady(false)

    if (Hls.isSupported()) {
      const hls = new Hls(hlsConfigRef.current)
      hls.loadSource(initialUrl)
      hls.attachMedia(video)
      hlsRef.current = hls

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsReady(true)
        if (mutedRef.current) video.muted = true
      })

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return

        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            logger().error('useHlsInstance: Fatal network error, attempting recovery', {
              details: data.details,
            })
            hls.startLoad()
            break
          case Hls.ErrorTypes.MEDIA_ERROR:
            logger().error('useHlsInstance: Fatal media error, attempting recovery', {
              details: data.details,
            })
            hls.recoverMediaError()
            break
          default:
            logger().error('useHlsInstance: Fatal error, cannot recover', {
              details: data.details,
              type: data.type,
            })
            hls.destroy()
            break
        }
      })

      return () => {
        hls.destroy()
        hlsRef.current = null
        setIsReady(false)
      }
    }

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari: native HLS support via the video element
      video.src = initialUrl
      const onMetadata = () => {
        setIsReady(true)
        if (mutedRef.current) video.muted = true
      }
      video.addEventListener('loadedmetadata', onMetadata)

      return () => {
        video.removeEventListener('loadedmetadata', onMetadata)
        setIsReady(false)
      }
    }

    logger().error('useHlsInstance: HLS is not supported in this browser and native HLS is unavailable')
  }, [videoRef])

  return { hlsRef, isReady }
}
